import api from "../utils/api";
import { IUser } from "../types";
import { IAdminOrder } from "../views/AdminOrdersView/types";
const ADMIN_BASE_PATH = "/admin";

interface IUpdateUser {
  _id: string;
  givenName: string;
  familyName: string;
  email: string;
  secondaryEmail?: string;
  password?: string;
  isAdmin: boolean;
  deliveryPrice?: number;
  excludeFromInvoiceReminders?: boolean;
}

interface INewUser {
  givenName: string;
  familyName: string;
  email: string;
  secondaryEmail?: string;
  password: string;
  isAdmin: boolean;
}

type ApiResponse<A> = {
  status: "success";
  data: A;
} | {
  status: "failed";
  message?: string;
};

export interface CustomersResponse {
  data: IUser[];
  total: number;
  page: number;
  limit: number;
}

interface GroupedOrder {
  deliveryLocationId: string;
  deliveryLocation: any;
  orders: any[];
  combinedNotes: string;
}

export interface GetAllOrdersResponse {
  orders: IAdminOrder[];
  groupedOrders?: GroupedOrder[];
  total: number;
  page?: number;
  limit?: number;
}

type AdminOrderAction = "confirm" | "decline" | "recreate-confirm";

// TODO: Add typing
export const AdminModel = {
  searchUsers: async ({
    query,
    page = 1,
    limit = 10,
  }: {
    query: string;
    page?: number;
    limit?: number;
  }): Promise<{ users: IUser[]; totalPages: number }> => {
    try {
      const response = await api.get<{ users: IUser[]; totalPages: number }>(
        `${ADMIN_BASE_PATH}/users/search`,
        {
          params: { query, page, limit },
        }
      );
      return response.data;
    } catch (e: any) {
      console.error("Failed to search users", e);
      throw e;
    }
  },
  getAllOrders: async ({
    page = 1,
    limit = 10,
    search = "",
    sortBy = "createdAt",
    sortOrder = "desc",
  }: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<GetAllOrdersResponse> => {
    const params: any = { page, limit };
    if (search) params.search = search;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;

    const response = await api.get<GetAllOrdersResponse>(
      `${ADMIN_BASE_PATH}/orders`,
      {
        params,
      }
    );
    return response.data;
  },
  respondToOrder: async (
    orderId: string,
    action: AdminOrderAction,
    notifyCustomer: boolean
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      `${ADMIN_BASE_PATH}/orders/${orderId}/${action}`,
      {
        notifyCustomer,
      }
    );

    return response.data;
  },
  getAllCustomers: async (
    page = 1,
    limit = 10,
    searchField?: string,
    searchValue?: string,
    sortBy?: string,
    sortOrder?: "asc" | "desc"
  ): Promise<CustomersResponse> => {
    try {
      const params: any = { page, limit };
      if (searchField) params.searchField = searchField;
      if (searchValue) params.searchValue = searchValue;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const response = await api.get<CustomersResponse>(`${ADMIN_BASE_PATH}/customers`, { params });
      return response.data;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  putUser: async (
    user: IUpdateUser
  ): Promise<IUser> => {
    try {
      const response = await api.put(
        `${ADMIN_BASE_PATH}/customers/${user._id}`,
        user
      );
      return response.data;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  newUser: async (user: INewUser): Promise<ApiResponse<IUser>> => {
    try {
      const response = await api.post<IUser>(
        `${ADMIN_BASE_PATH}/customers`,
        user
      );
      return { status: "success", data: response.data };
    } catch (e: any) {
      // Avoid logging the Axios request because it contains the submitted password.
      console.error("Failed to create user", e?.response?.status);

      if (e?.response?.status === 409) {
        return { status: "failed", message: "User already exists" };
      }
      return {
        status: "failed",
        message: e?.response?.data?.message,
      };
    }
  },
  deleteUser: async (id: IUser["_id"]): Promise<void> => {
    try {
      await api.delete<void>(`${ADMIN_BASE_PATH}/customers/${id}`);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  createCheckSalesReceipt: async (
    orderId: string,
    payload: {
      checkNumber: string;
      amount: number;
      noteText?: string;
    }
  ) => {
    const response = await api.post(
      `${ADMIN_BASE_PATH}/orders/${orderId}/check-sales-receipt`,
      payload
    );
    return response.data;
  },
};
